/**
 * SaaS Pricing Plans - Interactive Components
 * Handles quantity selection, cart integration, and contact modal
 */

// HubSpot Manager - Singleton for managing HubSpot script loading
const HubSpotManager = {
  scriptLoaded: false,
  loading: false,
  callbacks: [],

  loadScript() {
    return new Promise((resolve, reject) => {
      // If already loaded, resolve immediately
      if (this.scriptLoaded && window.hbspt) {
        resolve();
        return;
      }

      // If currently loading, queue the callback
      if (this.loading) {
        this.callbacks.push(resolve);
        return;
      }

      this.loading = true;

      // Load HubSpot Forms API v2
      const script = document.createElement('script');
      script.src = 'https://js.hs-scripts.com/242772093.js';
      script.defer = true;

      script.onload = () => {
        // Wait a bit for hbspt to be available
        const checkHbspt = setInterval(() => {
          if (window.hbspt && window.hbspt.forms) {
            clearInterval(checkHbspt);
            this.scriptLoaded = true;
            this.loading = false;
            resolve();
            // Execute any queued callbacks
            this.callbacks.forEach(cb => cb());
            this.callbacks = [];
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkHbspt);
          if (!this.scriptLoaded) {
            reject(new Error('HubSpot script load timeout'));
          }
        }, 10000);
      };

      script.onerror = () => {
        this.loading = false;
        reject(new Error('Failed to load HubSpot script'));
      };

      document.head.appendChild(script);
    });
  },

  async createForm(config) {
    try {
      await this.loadScript();
      if (window.hbspt && window.hbspt.forms) {
        window.hbspt.forms.create(config);
      }
    } catch (error) {
      console.error('HubSpot form creation error:', error);
    }
  }
};

// Quantity Selector Logic
class PricingPlanQuantity {
  constructor(planElement) {
    this.plan = planElement;
    this.minQty = parseInt(planElement.dataset.minQty) || 3;
    this.maxQty = parseInt(planElement.dataset.maxQty) || 10;
    this.unitPrice = parseInt(planElement.dataset.unitPrice) || 69800;
    this.subscriptionPrice = parseInt(planElement.dataset.subscriptionPrice) || 39900;
    this.currentQty = this.minQty;
    this.blockId = planElement.dataset.blockId;

    // Get DOM elements
    this.quantityDisplay = document.getElementById(`quantity-display-${this.blockId}`);
    this.totalDisplay = document.getElementById(`total-price-${this.blockId}`);
    this.renewalDisplay = document.getElementById(`renewal-price-${this.blockId}`);
    this.hiddenInput = planElement.querySelector('input[name="quantity"]');
    this.decreaseBtn = planElement.querySelector('.quantity-decrease');
    this.increaseBtn = planElement.querySelector('.quantity-increase');

    if (!this.quantityDisplay || !this.totalDisplay || !this.hiddenInput) {
      console.error('Required elements not found for quantity selector');
      return;
    }

    this.bindEvents();
    this.updateDisplay();
  }

  bindEvents() {
    // Bind +/- button clicks
    this.plan.querySelectorAll('[data-quantity-change]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const change = parseInt(e.currentTarget.dataset.quantityChange);
        this.changeQuantity(change);
      });
    });
  }

  changeQuantity(change) {
    const newQty = this.currentQty + change;

    if (newQty >= this.minQty && newQty <= this.maxQty) {
      this.currentQty = newQty;
      this.updateDisplay();
    }
  }

  updateDisplay() {
    // Update quantity display
    if (this.quantityDisplay) {
      this.quantityDisplay.textContent = this.currentQty;
    }

    // Update hidden input
    if (this.hiddenInput) {
      this.hiddenInput.value = this.currentQty;
    }

    // Calculate and display total price
    const totalCents = this.unitPrice * this.currentQty;
    if (this.totalDisplay) {
      this.totalDisplay.textContent = this.formatMoney(totalCents);
    }

    // Calculate and display renewal price
    if (this.renewalDisplay) {
      const renewalCents = this.subscriptionPrice * this.currentQty;
      this.renewalDisplay.textContent = this.formatMoney(renewalCents);
    }

    // Update button states
    this.updateButtonStates();
  }

  updateButtonStates() {
    // Disable decrease button at minimum
    if (this.decreaseBtn) {
      this.decreaseBtn.disabled = this.currentQty <= this.minQty;
    }

    // Disable increase button at maximum
    if (this.increaseBtn) {
      this.increaseBtn.disabled = this.currentQty >= this.maxQty;
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  }
}

// Add to Cart Handler
class AddToCartHandler {
  constructor(planElement) {
    this.plan = planElement;
    this.form = planElement.querySelector('.pricing-plan-form');
    this.submitBtn = planElement.querySelector('.add-to-cart-btn');
    this.variantId = planElement.dataset.variantId;

    if (!this.form || !this.submitBtn || !this.variantId) {
      console.error('Required elements not found for cart handler');
      return;
    }

    this.bindEvents();
  }

  bindEvents() {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  async handleSubmit(e) {
    e.preventDefault();

    const quantity = parseInt(this.plan.querySelector('input[name="quantity"]').value);

    if (!quantity || quantity < 1) {
      console.error('Invalid quantity');
      return;
    }

    // Show loading state
    this.submitBtn.classList.add('loading');
    this.submitBtn.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: this.variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const data = await response.json();
      console.log('Added to cart:', data);

      // Trigger cart refresh event (theme's cart drawer will listen for this)
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          open: true
        }
      }));

      // Show success state
      this.showSuccess();

    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError(error.message);
    } finally {
      this.submitBtn.classList.remove('loading');
      this.submitBtn.disabled = false;
    }
  }

  showSuccess() {
    const originalText = this.submitBtn.querySelector('.btn-text').textContent;
    const btnText = this.submitBtn.querySelector('.btn-text');

    btnText.textContent = 'Added to Cart!';
    this.submitBtn.classList.add('success');

    setTimeout(() => {
      btnText.textContent = originalText;
      this.submitBtn.classList.remove('success');
    }, 2000);
  }

  showError(message) {
    alert(`Unable to add to cart: ${message}\nPlease try again.`);
  }
}

// Contact Modal Handler
class ContactModal {
  constructor(triggerBtn) {
    this.trigger = triggerBtn;
    this.modalId = triggerBtn.getAttribute('data-modal-target');
    this.modal = document.getElementById(this.modalId);

    if (!this.modal) {
      console.error('Modal target not found:', this.modalId);
      return;
    }

    this.blockId = this.modal.getAttribute('data-block-id');
    this.closeBtns = this.modal.querySelectorAll('[data-close-modal]');
    this.formLoaded = false;

    this.bindEvents();
  }

  bindEvents() {
    // Open modal
    this.trigger.addEventListener('click', () => this.open());

    // Close modal via Close buttons and Overlay
    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden'; // Lock body scroll

    // Load HubSpot form if not already loaded
    if (!this.formLoaded) {
      this.loadHubSpotForm();
    }
  }

  close() {
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = ''; // Unlock body scroll
  }

  loadHubSpotForm() {
    // Check if custom HTML form is being used
    const customHtmlContainer = this.modal.querySelector('.custom-html-container');
    if (customHtmlContainer && customHtmlContainer.innerHTML.trim() !== '') {
      console.log('Using custom HTML form, skipping HubSpot load');
      this.formLoaded = true;
      return;
    }

    const containerId = `hubspot-form-${this.blockId}`;
    const container = document.getElementById(containerId);

    if (!container) {
      console.error('HubSpot form container not found:', containerId);
      return;
    }

    // Get HubSpot settings from data attributes (we'll need to add these)
    // For now, use the defaults
    const portalId = '242772093';
    const formId = 'f4972430-3e3a-48bc-bec5-da512469f279';
    const region = 'na2';

    console.log('Loading HubSpot form:', { portalId, formId, region, containerId });

    // Load and create HubSpot form
    HubSpotManager.createForm({
      region: region,
      portalId: portalId,
      formId: formId,
      target: `#${containerId}`,
      onFormReady: () => {
        console.log('HubSpot form ready');
      },
      onFormSubmit: () => {
        console.log('HubSpot form submitted');
      },
      onFormSubmitted: () => {
        console.log('HubSpot form submission complete');
        // Optionally close modal after submission
        setTimeout(() => {
          this.close();
        }, 2000);
      }
    });

    this.formLoaded = true;
  }
}

// Initialize all pricing plans on page load
document.addEventListener('DOMContentLoaded', function () {
  console.log('Initializing SaaS Pricing Plans...');

  // Initialize quantity selectors for quantity-based plans
  const quantityPlans = document.querySelectorAll('.pricing-plan-quantity');
  quantityPlans.forEach(plan => {
    new PricingPlanQuantity(plan);
    new AddToCartHandler(plan);
  });

  // Initialize contact modals
  const contactTriggers = document.querySelectorAll('.contact-sales-trigger');
  contactTriggers.forEach(btn => {
    new ContactModal(btn);
  });

  console.log(`Initialized ${quantityPlans.length} pricing plan(s) and ${contactTriggers.length} contact modal(s)`);
});

// Re-initialize when Shopify section is re-rendered in theme editor
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.classList.contains('saas-pricing-section')) {
      console.log('Re-initializing SaaS Pricing Plans in theme editor...');

      const quantityPlans = event.target.querySelectorAll('.pricing-plan-quantity');
      quantityPlans.forEach(plan => {
        new PricingPlanQuantity(plan);
        new AddToCartHandler(plan);
      });

      const contactTriggers = event.target.querySelectorAll('.contact-sales-trigger');
      contactTriggers.forEach(btn => {
        new ContactModal(btn);
      });
    }
  });
}
